import React from 'react';
import { TextField, TextFieldProps, InputAdornment, useTheme } from '@mui/material';
import { deepmerge } from '@mui/utils';
import { PounceStyleProps, extractPounceProps, mapPouncePropsToSx } from '../../utils';
import { Icon } from '../Icon';
import type { IconType } from '../../theme/icons';
import { Text } from '../Text';
import { isEmptyValue } from '../../utils/helpers';

export type TextIconProps = {
  color?: string;
  size?: 'x-small' | 'small' | 'medium' | 'large';
};

export interface TextInputProps
  extends Omit<TextFieldProps, 'variant' | 'multiline' | keyof PounceStyleProps>, PounceStyleProps {
  /** The label that is associated with this input */
  label: string;

  /** The decorating prefix used for decorating issues */
  prefix?: string;

  /** The decorating suffix used for decorating issues */
  suffix?: string;

  /** Whether the input has an invalid value */
  invalid?: boolean;

  /** Whether the input is required or not */
  required?: boolean;

  /** Whether the input is disabled or not */
  disabled?: boolean;

  /** The icon present on the input */
  icon?: IconType;

  /** The icon alignment */
  iconAlignment?: 'left' | 'right';

  /** Additional icon props */
  iconProps?: TextIconProps;

  /** Additional styles to apply (combined with pounce props) */
  sx?: TextFieldProps['sx'];
}

/**
 * TextInput component built on top of MUI TextField.
 *
 * Under the hood it renders an `input` element, so you
 * can pass it as many native attrs as you wish.
 *
 * Provides all the functionality from the original Pounce TextInput while leveraging
 * MUI's accessibility and interaction patterns.
 */
export const TextInput = React.forwardRef<HTMLDivElement, TextInputProps>(function TextInput(
  {
    label,
    invalid,
    required,
    disabled,
    hidden,
    id,
    name,
    value,
    prefix,
    suffix,
    icon,
    iconAlignment = 'right',
    iconProps = {},
    sx,
    ...rest
  },
  ref,
) {
  const { pounceProps, otherProps: otherPropsWithHandlers } = extractPounceProps(rest);
  const {
    onFocus: onFocusProp,
    onBlur: onBlurProp,
    onChange: onChangeProp,
    slotProps: slotPropsProp,
    ...otherProps
  } = otherPropsWithHandlers as typeof otherPropsWithHandlers & {
    onFocus?: React.FocusEventHandler<HTMLDivElement>;
    onBlur?: React.FocusEventHandler<HTMLDivElement>;
    onChange?: React.ChangeEventHandler<HTMLInputElement>;
    slotProps?: TextFieldProps['slotProps'];
  };
  const pounceStyles = mapPouncePropsToSx(pounceProps);
  const muiTheme = useTheme();
  const { tokens, typographyTokens } = muiTheme.palette;

  const leftIconLabelTranslateXBySize: Record<NonNullable<TextIconProps['size']>, number> = {
    'x-small': 34,
    small: 38,
    medium: 42,
    large: 46,
  };
  const iconSize = iconProps.size ?? 'medium';
  const labelTranslateX =
    icon && iconAlignment === 'left' ? leftIconLabelTranslateXBySize[iconSize] : null;

  const leftIconLabelSx =
    labelTranslateX !== null
      ? {
          '& .MuiInputLabel-root:not(.MuiInputLabel-shrink)': {
            transform: `translate(${labelTranslateX}px, 14px) scale(1)`,
          },
          '& .MuiInputLabel-root.MuiInputLabel-shrink': {
            transform: `translate(${labelTranslateX}px, 8px) scale(0.65)`,
          },
        }
      : {};
  const combinedSx = deepmerge(
    deepmerge(pounceStyles, leftIconLabelSx),
    sx || {},
  ) as TextFieldProps['sx'];

  const isControlled = value !== undefined;
  const [uncontrolledValue, setUncontrolledValue] = React.useState('');
  const effectiveValue = isControlled
    ? (value as string | number | readonly string[] | Record<string, unknown> | undefined)
    : uncontrolledValue;
  const isValueEmpty = isEmptyValue(effectiveValue);
  const [isFocused, setIsFocused] = React.useState(false);

  // Show prefix/suffix when input has value OR when focused
  const shouldShowPrefixSuffix = !isValueEmpty || isFocused;

  // Build start adornment (prefix or left icon)
  const startAdornment = React.useMemo(() => {
    const elements: React.ReactNode[] = [];

    if (prefix && shouldShowPrefixSuffix) {
      elements.push(
        <Text
          key="prefix"
          as="span"
          pr={1}
          sx={{
            paddingTop: '11px',
            fontWeight: typographyTokens?.fontWeights.medium,
            color: tokens?.content.text.accent[14].base,
            marginRight: prefix ? '-8px' : 0, // This is the workaround of the inconsistent spacing coming from the icon as a prefix
            marginTop: '1px',
          }}
        >
          {prefix}
        </Text>,
      );
    }

    if (icon && iconAlignment === 'left') {
      elements.push(
        <Icon
          key="icon-left"
          type={icon}
          size={iconProps.size || 'medium'}
          sx={{
            display: 'flex',
            alignItems: 'center',
            marginLeft: prefix && shouldShowPrefixSuffix ? '15px' : 0,
            // Line below insure passed color is in priority, TODO: update Icon component and remove important tag
            ...(iconProps.color && { color: `${iconProps.color} !important` }),
          }}
        />,
      );
    }

    return elements.length > 0 ? (
      <InputAdornment position="start">{elements}</InputAdornment>
    ) : null;
  }, [prefix, icon, iconAlignment, iconProps, tokens, typographyTokens, shouldShowPrefixSuffix]);

  // Build end adornment (suffix or right icon)
  const endAdornment = React.useMemo(() => {
    const elements: React.ReactNode[] = [];

    if (icon && iconAlignment === 'right') {
      elements.push(
        <Icon
          key="icon-right"
          type={icon}
          size={iconProps.size || 'medium'}
          sx={{
            // Line below insure passed color is in priority, TODO: update Icon component and remove important tag
            ...(iconProps.color && { color: `${iconProps.color} !important` }),
          }}
          {...iconProps}
        />,
      );
    }

    if (suffix && shouldShowPrefixSuffix) {
      elements.push(
        <Text
          key="suffix"
          as="span"
          sx={{
            paddingTop: '11px',
            fontWeight: typographyTokens?.fontWeights.medium,
            color: tokens?.content.text.accent[14].base,
            marginTop: '1px',
          }}
        >
          {suffix}
        </Text>,
      );
    }

    return elements.length > 0 ? <InputAdornment position="end">{elements}</InputAdornment> : null;
  }, [suffix, icon, iconAlignment, iconProps, tokens, typographyTokens, shouldShowPrefixSuffix]);

  const mergedSlotProps = React.useMemo(
    () =>
      deepmerge(
        {
          inputLabel: {
            shrink: isFocused || !isValueEmpty,
          },
          input: {
            'aria-invalid': invalid,
            startAdornment,
            endAdornment,
          },
          htmlInput: {
            'aria-required': required,
          },
        },
        slotPropsProp ?? {},
      ),
    [isFocused, isValueEmpty, invalid, required, startAdornment, endAdornment, slotPropsProp],
  );

  return (
    <TextField
      ref={ref}
      id={id}
      name={name}
      label={label}
      {...(isControlled ? { value } : { value: uncontrolledValue })}
      type="text"
      fullWidth
      required={required}
      disabled={disabled}
      error={invalid}
      sx={combinedSx}
      hidden={hidden}
      onFocus={e => {
        setIsFocused(true);
        onFocusProp?.(e);
      }}
      onBlur={e => {
        setIsFocused(false);
        onBlurProp?.(e);
      }}
      onChange={e => {
        if (!isControlled) {
          setUncontrolledValue(e.target.value ?? '');
        }
        onChangeProp?.(e);
      }}
      slotProps={mergedSlotProps}
      {...otherProps}
    />
  );
});
